// The results file: one flat record per final review (analysisResults/{itemId}). The Cloud Function
// functions/src/analysisResults.ts writes it on the server; resultFrom below is the SAME mapping (keep them equal),
// used by the preview and to describe "what is saved".
import { SECTION } from './sections';
import { fromEngine, toReviewDoc } from './reviewModel';
import { fromPiece, toPulsedDoc } from './pulsedModel';

const TONAL = 'cetalingua.callReview/1';
const PULSED = 'cetalingua.pulsedReview/1';

const countBy = (xs, key) => xs.reduce((o, x) => { const k = String(x?.[key] ?? 'other'); o[k] = (o[k] || 0) + 1; return o; }, {});

export function resultFrom(itemId, item, review, editsId) {
  const final = review.species?.final || {};
  const base = {
    itemId, category: item.category ?? review.section ?? null, public: item.public === true,
    stationId: item.stationId ?? null, fileId: item.fileId ?? review.source?.fileId ?? null,
    recordedAt: item.recordedAt ?? null, schema: review.schema ?? null,
    engineVersion: review.source?.engineVersion ?? item.engineVersion ?? null, editsId,
    status: review.status ?? null, dismissReason: review.dismiss?.reason ?? null,
    species: { id: final.id ?? null, label: final.label ?? null, source: final.source ?? null, engineId: review.species?.engine?.id ?? null },
    code: review.label?.code ?? null, engineCode: review.label?.engineCode ?? null,
    codeChanged: (review.label?.code ?? null) !== (review.label?.engineCode ?? null),
    decoder: item.reviewedBy && item.reviewedBy !== item.doneBy ? item.reviewedBy : null,
    finalBy: item.doneBy ?? review.by ?? null,
    finalAt: item.doneAt ?? null,
  };
  if (review.schema === TONAL) {
    const m = review.measurements || {};
    const values = { ...(m.engine || {}), ...(m.fromDrawing || {}), ...(m.person || {}) };
    const changed = Object.keys(values).filter((k) => String((m.engine || {})[k] ?? '') !== String(values[k]));
    return { ...base, broad: review.label?.broad ?? null, engineBroad: review.label?.engineBroad ?? null,
      call: review.call ? { t0: review.call.t0, t1: review.call.t1, f0: review.call.f0, f1: review.call.f1 } : null,
      measurements: values, changed,
      lines: countBy(review.lines || [], 'kind'), boxes: countBy(review.boxes || [], 'kind'), parts: countBy(review.parts || [], 'k') };
  }
  if (review.schema === PULSED) {
    const r = review.result || {}; const e = review.edits || {}; const sm = r.engine?.summary || {};
    const added = e.addedClicks || [];
    return { ...base, counts: r.counts || {},
      animals: { best: sm.animals_best ?? null, min: sm.animals_min ?? null, max: sm.animals_max ?? null },
      trains: (r.trains || []).map((t) => ({ id: t.id, type: t.type, n: t.n, dur: t.dur ?? null, iciMedian: t.iciMedian ?? null,
        iciCv: t.iciCv ?? null, trend: t.trend ?? null, rhythm: t.rhythm ?? null, animal: t.animal ?? null })),
      codas: (e.codas || []).map((c) => ({ t0: c.t0, t1: c.t1, n: c.n, rhythm: c.rhythm, ici: c.ici })),
      creaks: e.creaks || [],
      edits: { added: added.length, found: added.filter((a) => a.found === 'find missing pulses').length,
        removed: (e.removedClicks || []).length, relabelled: (e.relabelled || []).length,
        trainChanged: (e.trainChanged || []).length, burstsMarked: (e.burstsMarked || []).length,
        burstsUnmarked: (e.burstsUnmarked || []).length } };
  }
  return base;
}

// recording time from the file name (station_..._YYYYMMDD_HHMMSS)
export function recordedFromFile(file) {
  const m = String(file || '').match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z` : null;
}

// PREVIEW: what each example would give if confirmed as the engine found it (shown as "example — engine values")
export function exampleResults(bundles) {
  const out = {};
  Object.entries(bundles).forEach(([key, b]) => {
    const section = SECTION[key]; if (!section || key === 'unsorted') return;
    out[key] = (b.calls || []).map((c, j) => {
      const pulsed = section.signal === 'pulsed';
      const review = pulsed ? toPulsedDoc(c, fromPiece(c), section, {}, { status: 'confirmed' })
        : toReviewDoc(c, fromEngine(c, section), section, {}, { status: 'confirmed' });
      const r = resultFrom(`example-${key}-${c.id}`, { category: key, public: true, stationId: j % 2 ? 'demo-station' : null,
        recordedAt: recordedFromFile(c.file) }, review, 'example');
      return { ...r, example: true, finalBy: 'example (engine values, not reviewed)' };
    });
  });
  return out;
}

// what is saved for each final review — shown in the Results view
export const SAVED_COMMON = [
  ['itemId', 'the piece / call'], ['category', 'section'], ['public, stationId, fileId', 'where it comes from'],
  ['recordedAt', 'when it was recorded'], ['status, dismissReason', 'confirmed, or dismissed and why'],
  ['species {id, label, source, engineId}', 'final species and what the engine said'],
  ['code, engineCode, codeChanged', 'final call / click code and the engine’s'],
  ['decoder, finalBy, finalAt', 'who reviewed, who made it final, when'], ['schema, engineVersion, editsId', 'the full review it came from'],
];
export const SAVED_TONAL = [
  ['broad, engineBroad', 'broad class (final / engine)'], ['call {t0, t1, f0, f1}', 'the call box'],
  ['measurements', 'every measurement: engine values, replaced by values from the drawing and by typed values'],
  ['changed', 'which measurements differ from the engine'], ['lines, boxes, parts', 'how many F0 / harmonic lines, boxes (by kind), parts'],
];
export const SAVED_PULSED = [
  ['counts', 'clicks, clicks in trains, trains, bursts, echoes, other sounds, added, removed'],
  ['animals {best, min, max}', 'engine estimate of animals'],
  ['trains [ ]', 'every train / burst: type, clicks, length, ICI median, CV, trend, rhythm, animal'],
  ['codas [ ], creaks [ ]', 'marked codas (clicks, rhythm, intervals) and creaks (sperm whales)'],
  ['edits', 'pulses added (by hand / found), removed, relabelled, moved between trains, bursts marked / unmarked'],
];

const flat = (o, p = '', out = {}) => {
  Object.entries(o || {}).forEach(([k, v]) => {
    const key = p ? `${p}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flat(v, key, out);
    else out[key] = Array.isArray(v) ? (v.length && typeof v[0] === 'object' ? v.length : v.join(' ')) : v;
  });
  return out;
};
/** one row per result; nested values flattened (measurements.f0_start, counts.trains, ...); lists of objects -> count */
export function toCSV(rows) {
  const flats = rows.map((r) => { const { example, ...x } = r; return flat(x); });
  const cols = [...new Set(flats.flatMap((f) => Object.keys(f)))];
  const esc = (v) => { if (v === null || v === undefined) return ''; const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [cols.join(','), ...flats.map((f) => cols.map((c) => esc(f[c])).join(','))].join('\n');
}
/** pulsed trains as their own table (one row per train) */
export function trainsCSV(rows) {
  const out = ['itemId,recordedAt,species,train,type,clicks,duration_s,ici_median_ms,ici_cv,trend,rhythm,animal'];
  rows.forEach((r) => (r.trains || []).forEach((t) => out.push([r.itemId, r.recordedAt || '', r.species?.label || '', t.id, t.type, t.n, t.dur, t.iciMedian, t.iciCv, t.trend, t.rhythm, t.animal]
    .map((v) => (v === null || v === undefined ? '' : String(v).includes(',') ? `"${v}"` : v)).join(','))));
  return out.join('\n');
}

/** summary export (Decoder plan): confirmed / dismissed counts per recording day, species and code */
export function summaryCSV(rows) {
  const m = new Map();
  rows.forEach((r) => { const k = [String(r.recordedAt || '').slice(0, 10) || 'unknown', r.species?.label || '', r.code || ''].join('\u0000');
    const v = m.get(k) || [0, 0]; if (r.status === 'dismissed') v[1] += 1; else v[0] += 1; m.set(k, v); });
  const esc = (v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  return ['date,species,code,confirmed,dismissed', ...[...m.entries()].sort().map(([k, [a, b]]) => [...k.split('\u0000'), a, b].map(esc).join(','))].join('\n');
}
